import { AppShell } from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useStaff } from '@/lib/staff';
import { cn } from '@/lib/utils';
import {
  ArrowRight,
  BarChart3,
  Building2,
  Clock,
  KeyRound,
  ShieldCheck,
  Sparkles,
  UsersRound,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

function firstName(name: string | null, email: string): string {
  if (name) {
    const f = name.trim().split(/\s+/)[0];
    if (f) return f;
  }
  const local = email.split('@')[0] ?? '';
  // Strip common separators and capitalize the first chunk.
  const seg = local.split(/[._-]/)[0] ?? local;
  return seg ? seg.charAt(0).toUpperCase() + seg.slice(1) : 'there';
}

function timeOfDay(): string {
  const h = new Date().getHours();
  if (h < 5) return 'Working late';
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

interface BillingMe {
  hasSubscription: boolean;
  subscription: { status: string; trialEndsAt: number | null } | null;
}

export function HomePage() {
  const { getAccessToken, sessionKey, me } = useAuth();
  const { isPlatformAdmin } = useStaff();
  const [billing, setBilling] = useState<BillingMe | null>(null);
  const [passwordBannerDismissed, setPasswordBannerDismissed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('mg-set-password-dismissed') === '1';
  });
  const dismissPasswordBanner = () => {
    setPasswordBannerDismissed(true);
    if (typeof window !== 'undefined') {
      localStorage.setItem('mg-set-password-dismissed', '1');
    }
  };

  useEffect(() => {
    if (!sessionKey) return;
    void (async () => {
      try {
        const r = await api<BillingMe>('/billing/me', { accessToken: getAccessToken() });
        setBilling(r);
      } catch {
        /* non-blocking */
      }
    })();
  }, [sessionKey, getAccessToken]);

  if (!me?.user) return null;

  const greeting = timeOfDay();
  const fname = firstName(me.user.name, me.user.email);
  const brandName = me.brands.find((t) => t.brandId === me.activeBrandId)?.name ?? 'your workspace';
  const isReviewer = me.activeRole === 'owner' || me.activeRole === 'admin';

  return (
    <AppShell width="lg">
      <section className="space-y-8">
        <header className="space-y-1">
          <p className="text-sm text-muted-foreground">{greeting},</p>
          <h1 className="text-3xl font-semibold tracking-tight text-fg sm:text-4xl">{fname}.</h1>
          <p className="text-sm text-muted-foreground">
            You&apos;re working in <strong className="text-fg-soft">{brandName}</strong>.
          </p>
        </header>

        <TrialBanner billing={billing} />

        {!me.user.hasPassword && !passwordBannerDismissed ? (
          <Card className="border-brand/30 bg-brand-soft/30">
            <CardContent className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-brand text-white">
                  <KeyRound className="size-4" />
                </div>
                <div className="space-y-0.5">
                  <p className="text-sm font-medium text-fg">Set a password for faster sign-ins</p>
                  <p className="text-xs text-muted-foreground">
                    Magic links still work, but a password skips the email roundtrip.
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button asChild size="sm">
                  <Link to="/set-password">
                    Set password <ArrowRight className="size-3.5" />
                  </Link>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={dismissPasswordBanner}
                  aria-label="Dismiss"
                  className="text-muted-foreground"
                >
                  ✕
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : null}

        <div className={cn('grid gap-3 sm:grid-cols-2', me.brands.length > 1 && 'lg:grid-cols-3')}>
          <ActionCard
            to="/analytics"
            icon={BarChart3}
            title="Open analytics"
            description="View your dashboards, KPIs and pacing."
            primary
          />
          {me.brands.length > 1 ? (
            <ActionCard
              to="/agency"
              icon={Building2}
              title="Agency overview"
              description={`Cross-client KPIs for ${me.brands.length} workspaces.`}
            />
          ) : null}
          {isPlatformAdmin ? (
            <ActionCard
              to="/admin"
              icon={ShieldCheck}
              title="Admin console"
              description="Manage workspaces, data sources, and dashboards."
            />
          ) : isReviewer ? (
            <ActionCard
              to="/admin/signups"
              icon={UsersRound}
              title="Review signups"
              description="Approve people requesting access to this workspace."
            />
          ) : (
            <ActionCard
              to="/analytics"
              icon={Sparkles}
              title="What's new"
              description="Recent updates and tips for getting the most out of Adholics."
            />
          )}
        </div>
      </section>
    </AppShell>
  );
}

function TrialBanner({ billing }: { billing: BillingMe | null }) {
  if (!billing?.subscription) return null;
  const sub = billing.subscription;
  if (sub.status !== 'trialing' || !sub.trialEndsAt) return null;
  const ms = sub.trialEndsAt * 1000 - Date.now();
  const days = Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
  if (days > 3) return null; // Only nag in the final 3 days.
  const tone =
    days === 0
      ? 'border-danger/30 bg-danger-soft text-fg'
      : 'border-warning/30 bg-warning-soft text-fg';
  return (
    <Card className={cn(tone)}>
      <CardContent className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <Clock className="mt-0.5 size-5 shrink-0" />
          <div className="space-y-0.5">
            <p className="text-sm font-medium text-fg">
              {days === 0
                ? 'Your trial ends today.'
                : `Your trial ends in ${days} ${days === 1 ? 'day' : 'days'}.`}
            </p>
            <p className="text-xs text-muted-foreground">
              Add payment to keep your dashboards live without interruption.
            </p>
          </div>
        </div>
        <Button asChild size="sm">
          <Link to="/billing">
            Upgrade <ArrowRight className="size-3.5" />
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}

interface ActionCardProps {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  primary?: boolean;
}

function ActionCard({ to, icon: Icon, title, description, primary }: ActionCardProps) {
  return (
    <Link
      to={to}
      className={cn(
        'group relative overflow-hidden rounded-xl border border-line bg-surface p-5 transition-all hover:-translate-y-0.5 hover:border-brand hover:shadow-elevated',
        primary && 'bg-gradient-to-br from-surface to-brand-soft/40',
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div
          className={cn(
            'flex size-10 items-center justify-center rounded-lg',
            primary ? 'bg-brand text-white' : 'bg-surface-2 text-fg-soft',
          )}
        >
          <Icon className="size-5" />
        </div>
        <ArrowRight className="size-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-brand" />
      </div>
      <div className="mt-4 space-y-1">
        <p className="text-base font-semibold tracking-tight text-fg">{title}</p>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
    </Link>
  );
}
